/**
 * SplitMix64 — the explicitly implemented seeded generator required by
 * spec §6.5. BigInt gives exact 64-bit wraparound semantics; the Swift
 * mirror uses UInt64 overflow operators and produces identical output.
 */

const MASK64 = (1n << 64n) - 1n;
const GOLDEN = 0x9e3779b97f4a7c15n;
const MIX1 = 0xbf58476d1ce4e5b9n;
const MIX2 = 0x94d049bb133111ebn;
const TWO_POW_MINUS_53 = 1 / 9007199254740992;

export class SplitMix64 {
  private s: bigint;

  constructor(seed: bigint) {
    this.s = seed & MASK64;
  }

  nextUInt64(): bigint {
    this.s = (this.s + GOLDEN) & MASK64;
    let z = this.s;
    z = ((z ^ (z >> 30n)) * MIX1) & MASK64;
    z = ((z ^ (z >> 27n)) * MIX2) & MASK64;
    return (z ^ (z >> 31n)) & MASK64;
  }

  /** Uniform double in [0, 1) using the top 53 bits — exact in both engines. */
  nextDouble(): number {
    return Number(this.nextUInt64() >> 11n) * TWO_POW_MINUS_53;
  }
}

/**
 * Deterministic daily-challenge seed: UTC date + version salt (spec §21).
 * Scrambled through SplitMix64 so consecutive dates are uncorrelated.
 */
export function dailySeed(utcYear: number, utcMonth1: number, utcDay: number): bigint {
  const salt = 0xec0d0d6e00000000n; // "ECO DODGE" v1 salt — new salt = new bucket
  const date = BigInt(utcYear * 10000 + utcMonth1 * 100 + utcDay);
  const rng = new SplitMix64(salt ^ date);
  return rng.nextUInt64();
}

/** Random run seed for Classic; sourced outside the engine to keep it pure. */
export function randomSeed(): bigint {
  const a = BigInt(Math.floor(Math.random() * 0xffffffff));
  const b = BigInt(Math.floor(Math.random() * 0xffffffff));
  return ((a << 32n) | b) & MASK64;
}
